import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X } from "lucide-react"
import { useClickOutside } from "../../travel_book/avia_v3/hooks"
import ChatListSidebar from "./ChatListSidebar"
import ChatHeader from "./ChatHeader"
import MessageBubble from "./MessageBubble"
import ComposeArea from "./ComposeArea"
import ChatInfoSidebar from "./ChatInfoSidebar"
import {
  FOR_ADMIN_KEY,
  MOTION_VARIANTS,
  CHAT_TABS,
  TAB_CAPABILITIES,
  TAB_FILTER_DEFAULTS,
  TAB_FILTER_FIELDS,
  TAB_ORDER,
  buildStandardThemes,
  STANDARD_THEME_IDS,
} from "./constants"
import PinnedMessage from "./PinnedMessage"
import EmptyState from "./EmptyState"
import DateDivider from "./DateDivider"
import { formatDateDivider } from "../../utils/date/format"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import {
  useCreateNotifyMutation,
  useGetChatMetaQuery,
  useGetChatNotifiesQuery,
  useGetListFlightChangesQuery,
  useLazyGetChatDetailQuery,
  useLazyGetChatNotifiesQuery,
  useLazyGetFlightChangesDetailQuery,
  useLazyGetListFlightChangesQuery,
  useToggleReadMessageMutation,
  useUpdateNotifyDepartmentMutation,
  useUpdateNotifyMessageMutation,
} from "../../../app/services/book/notifies"

import {
  useGetLeadNotifiesQuery,
  useLazyGetLeadNotifiesQuery,
  useLazyGetLeadChatDetailQuery,
  useCreateLeadNotifyMutation,
  useToggleLeadReadMessageMutation,
  useGetLeadChatMetaQuery,
} from "../../../app/services/book/leadNotifies"

import {
  selectNotifiesList,
  selectNotifiesHasMore,
  resetNotifies,
  setNotifies,
  selectNotifiesChatMeta,
} from "../../../app/reducers/book/notifies"

import { useChatMessages, useChatPanelResize, useChatSidebarResize } from "../hooks"
import { useEventBusModal } from "../../../shared/hooks"
import { selectDefaultDepartament } from "../../../app/reducers/admin/departamentsSlice"
import { READ_MESSAGE_STATUS } from "../../utils/variables/chat"
import useChatComposeResize from "../hooks/useChatComposeResize"
import { useDraftPersist } from "../hooks/useDraftPersist"

const POLL_INTERVAL = 5 * 60 * 1000

const LAST_FILTERS_KEY = (tab) => `notifies_last_filters_${tab}`

const loadLastFilters = (tab) => {
  try {
    const raw = localStorage.getItem(LAST_FILTERS_KEY(tab))
    return raw ? JSON.parse(raw) : null
  } catch (_) {
    return null
  }
}

const saveLastFilters = (tab, filters) => {
  try {
    const { page, per_page, bundle_id, lead_id, ...rest } = filters
    localStorage.setItem(LAST_FILTERS_KEY(tab), JSON.stringify(rest))
  } catch (_) {}
}

const clearLastFilters = (tab) => {
  try {
    localStorage.removeItem(LAST_FILTERS_KEY(tab))
  } catch (_) {}
}

const buildInitialFilters = (tab) => {
  const defaults = TAB_FILTER_DEFAULTS[tab] ?? TAB_FILTER_DEFAULTS[CHAT_TABS.ALL]
  const saved = loadLastFilters(tab)
  if (!saved) return { ...defaults }
  return { ...defaults, ...saved, page: 1, per_page: defaults.per_page, bundle_id: "", lead_id: "" }
}

// Универсальный идентификатор чата — bundle_id для бандлов, lead_id для лидов
const chatId = (chat) => chat?.bundle_id ?? chat?.lead_id ?? null

// ---------------------------------------------------------------------------
// MessageFilter
// ---------------------------------------------------------------------------
const MessageFilter = ({ departments, filterDepts, setFilterDepts, messages }) => {
  const { t } = useTranslation()
  const counts = useMemo(() => {
    const map = {}
    messages.forEach((msg) => {
      if (msg.department_id) map[msg.department_id] = (map[msg.department_id] || 0) + 1
    })
    return map
  }, [messages])
  const activeDepts = useMemo(
    () => (departments || []).filter((d) => counts[d.id] > 0),
    [departments, counts]
  )
  if (activeDepts.length === 0) return null
  const toggle = (id) => {
    const sid = String(id)
    setFilterDepts((prev) => (prev.includes(sid) ? prev.filter((d) => d !== sid) : [...prev, sid]))
  }
  const isActive = (id) => filterDepts.includes(String(id))
  const hasFilter = filterDepts.length > 0
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-white border-b border-slate-100 overflow-x-auto scrollbar-none flex-shrink-0">
      <button
        onClick={() => setFilterDepts([])}
        className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
          !hasFilter
            ? "bg-slate-800 text-white shadow-sm"
            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
        }`}
      >
        {t("admin.all", "Все")}
        <span className={`ml-1.5 text-[10px] ${!hasFilter ? "text-white/70" : "text-slate-400"}`}>
          {messages.length}
        </span>
      </button>
      {activeDepts.map((dept) => (
        <button
          key={dept.id}
          onClick={() => toggle(dept.id)}
          className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
            isActive(dept.id)
              ? "bg-blue-500 text-white shadow-sm"
              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >
          {dept.name}
          <span
            className={`ml-1.5 text-[10px] ${isActive(dept.id) ? "text-white/70" : "text-slate-400"}`}
          >
            {counts[dept.id]}
          </span>
        </button>
      ))}
      {hasFilter && (
        <button
          onClick={() => setFilterDepts([])}
          className="flex-shrink-0 p-1 hover:bg-red-50 rounded-full transition-colors ml-auto"
        >
          <X className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab hooks — стабильные ссылки через useMemo по activeTab
// ---------------------------------------------------------------------------

const useTabListQuery = (activeTab, filters, skip) => {
  const allQuery = useGetChatNotifiesQuery(filters, {
    skip: skip || activeTab !== CHAT_TABS.ALL,
    refetchOnMountOrArgChange: true,
  })
  const flightQuery = useGetListFlightChangesQuery(filters, {
    skip: skip || activeTab !== CHAT_TABS.FLIGHT_CHANGES,
    refetchOnMountOrArgChange: true,
  })
  const leadsQuery = useGetLeadNotifiesQuery(filters, {
    skip: skip || activeTab !== CHAT_TABS.LEADS,
    refetchOnMountOrArgChange: true,
  })

  switch (activeTab) {
    case CHAT_TABS.FLIGHT_CHANGES:
      return flightQuery
    case CHAT_TABS.LEADS:
      return leadsQuery
    default:
      return allQuery
  }
}

const useTabDetailQuery = (activeTab) => {
  const [getChatDetail] = useLazyGetChatDetailQuery()
  const [getFlightChangesDetail] = useLazyGetFlightChangesDetailQuery()
  const [getLeadChatDetail] = useLazyGetLeadChatDetailQuery()

  return useMemo(() => {
    switch (activeTab) {
      case CHAT_TABS.FLIGHT_CHANGES:
        return getFlightChangesDetail
      case CHAT_TABS.LEADS:
        return getLeadChatDetail
      default:
        return getChatDetail
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])
}

const useTabLazyListQuery = (activeTab) => {
  const [getChatNotifiesLazy] = useLazyGetChatNotifiesQuery()
  const [getFlightChangesLazy] = useLazyGetListFlightChangesQuery()
  const [getLeadNotifiesLazy] = useLazyGetLeadNotifiesQuery()

  return useMemo(() => {
    switch (activeTab) {
      case CHAT_TABS.FLIGHT_CHANGES:
        return getFlightChangesLazy
      case CHAT_TABS.LEADS:
        return getLeadNotifiesLazy
      default:
        return getChatNotifiesLazy
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])
}

const useTabCreateNotify = (activeTab) => {
  const [createBundleNotify, bundleState] = useCreateNotifyMutation()
  const [createLeadNotify, leadState] = useCreateLeadNotifyMutation()

  return useMemo(() => {
    switch (activeTab) {
      case CHAT_TABS.LEADS:
        return [createLeadNotify, leadState]
      default:
        return [createBundleNotify, bundleState]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])
}

const useTabToggleRead = (activeTab) => {
  const [toggleBundle] = useToggleReadMessageMutation()
  const [toggleLead] = useToggleLeadReadMessageMutation()

  return useMemo(() => {
    switch (activeTab) {
      case CHAT_TABS.LEADS:
        return toggleLead
      default:
        return toggleBundle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])
}

// ---------------------------------------------------------------------------
// ChatBox
// ---------------------------------------------------------------------------
const ChatBox = ({ modalId, isAdmin, currentCompanyId, currentUserId, themeName }) => {
  const IS_TRAVELON = themeName === "novatravel"
  const OPEN_EVENT = `modal:${modalId}:open`
  const CLOSE_EVENT = `modal:${modalId}:close`

  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const locale = i18n.locale

  const { isOpen, setIsOpen, payload, openTick } = useEventBusModal(OPEN_EVENT, CLOSE_EVENT, [
    modalId,
  ])

  const { bundleId, typeNotify, providerId, remoteOrder, leadId } = payload ?? {}

  const anchorLoadingRef = useRef(false)
  if (isOpen && (bundleId || leadId)) {
    anchorLoadingRef.current = true
  }

  const { list: notifiesList, meta: notifiesMeta } = useSelector(selectNotifiesList)
  const hasMore = useSelector(selectNotifiesHasMore)
  const {
    departments,
    messagethemes: messagethemesData,
    total_unread,
    leads_unread,
    settings,
  } = useSelector(selectNotifiesChatMeta)

  const defaultDepartment = useSelector(selectDefaultDepartament)

  const [selectedChat, setSelectedChat] = useState(null)
  const [isMobileView, setIsMobileView] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [showSidebar, setShowSidebar] = useState(false)
  const [message, setMessage] = useState("")
  const [department, setDepartment] = useState(null)
  const [attachedFiles, setAttachedFiles] = useState([])
  const [messageTheme, setMessageTheme] = useState(null)
  // isAnchorLoading state остаётся только для UI (спиннер в ChatListSidebar).
  // Для skip в useTabListQuery используется anchorLoadingRef.current.
  const [isAnchorLoading, setIsAnchorLoading] = useState(false)
  const [filterDepts, setFilterDepts] = useState([])
  const [sendMode, setSendMode] = useState("all")
  const [replyTo, setReplyTo] = useState(null)

  const [activeTab, setActiveTab] = useState(CHAT_TABS.ALL)
  const [filters, setFilters] = useState(() => buildInitialFilters(CHAT_TABS.ALL))

  const tabCapabilities = TAB_CAPABILITIES[activeTab] ?? TAB_CAPABILITIES[CHAT_TABS.ALL]

  const chatRef = useRef(null)
  const sidebarRef = useRef(null)
  const messagesEndRef = useRef(null)
  const firstUnreadRef = useRef(null)
  const prevMsgCountRef = useRef(0)
  const sentinelRef = useRef(null)
  const scrolledForBundleRef = useRef(null)
  const scrollTimerRef = useRef(null)

  // Хелпер — выставляет ref и state синхронно.
  // ref блокирует запрос немедленно (читается во время рендера),
  // state обновляет UI (спиннер) после рендера.
  const setAnchorLoading = useCallback((val) => {
    anchorLoadingRef.current = val
    setIsAnchorLoading(val)
  }, [])

  // skip читает ref — срабатывает синхронно в том же рендере
  const { isFetching } = useTabListQuery(activeTab, filters, !isOpen || anchorLoadingRef.current)

  const getChatDetail = useTabDetailQuery(activeTab)
  const getListLazy = useTabLazyListQuery(activeTab)

  // Отдельный lazy-запрос для лидов — нужен независимо от activeTab
  const [getLeadNotifiesLazy] = useLazyGetLeadNotifiesQuery()
  // FIX: объявляем getLeadChatDetailDirect на верхнем уровне компонента,
  // чтобы передавать его явно в fetchMessages внутри openByLeadAnchor,
  // не завися от того, успел ли activeTab обновиться и обновился ли getChatDetailRef
  const [getLeadChatDetailDirect] = useLazyGetLeadChatDetailQuery()

  const [createNotify, { isFetching: createIsFetching }] = useTabCreateNotify(activeTab)
  const toggleReadMsg = useTabToggleRead(activeTab)

  useGetChatMetaQuery(undefined, { skip: !isOpen })

  const [updateDepartment] = useUpdateNotifyDepartmentMutation()
  const [updateNotifyMessage] = useUpdateNotifyMessageMutation()

  const { sidebarWidth, setSidebarWidth, isResizing, startResize, collapse, expand } =
    useChatSidebarResize(15)
  const { chatWidth, isResizingChat, startResizeChat } = useChatPanelResize(80)

  const { composeHeight, isResizingCompose, startResizeCompose } = useChatComposeResize(200)

  const { saveDraftDebounced, loadDraft, clearDraft } = useDraftPersist()

  useEffect(() => {
    if (bundleId || leadId) setSidebarWidth(0)
    else setSidebarWidth((prev) => (prev === 0 ? 15 : prev))
  }, [bundleId, leadId])

  const {
    getMessages,
    fetchMessages,
    isLoading,
    sendMessage,
    toggleEdit,
    saveEdit,
    cancelEdit,
    changeDept,
    toggleReadStatus,
    markAsRead,
  } = useChatMessages({
    departments,
    getChatDetail,
    createNotify,
    locale,
    currentCompanyId,
    currentUserId,
    sortByLastMessage: settings?.sort_by_last_message,
  })

  const selectedChatId = chatId(selectedChat)
  const allMessages = selectedChat ? getMessages(selectedChatId) : []
  const currentMessages =
    filterDepts.length > 0
      ? allMessages.filter((msg) => filterDepts.includes(String(msg.department_id)))
      : allMessages
  const currentMessagesLength = currentMessages.length

  const firstUnreadIndex = useMemo(
    () => currentMessages.findIndex((msg) => msg.status !== READ_MESSAGE_STATUS && !msg.isOwn),
    [currentMessages]
  )

  const useDistribution = settings?.distribution_of_notifications_by_departments
  const hideStandard = settings?.hide_standard_notification_themes

  const isCurrentChatLoading = isLoading(selectedChatId)

  const filteredMessagethemes = useMemo(() => {
    if (tabCapabilities.isLeads) return []

    const standard = hideStandard ? [] : buildStandardThemes(t, isAdmin)
    const custom = (messagethemesData || []).filter((theme) => {
      if (IS_TRAVELON && theme.only_admin && !isAdmin) return false
      if (useDistribution) {
        if (!department) return false
        return Number(theme.departament_id) === Number(department)
      }
      return true
    })
    return [...standard, ...custom]
  }, [
    messagethemesData,
    department,
    isAdmin,
    useDistribution,
    hideStandard,
    t,
    tabCapabilities.isLeads,
  ])

  useEffect(() => {
    if (isAdmin) return
    if (!selectedChat || isCurrentChatLoading) return

    const timer = setTimeout(() => {
      markAsRead(selectedChatId, { toggleReadMsg, currentUserId })
    }, 0)

    return () => clearTimeout(timer)
  }, [selectedChatId, isCurrentChatLoading])

  useEffect(() => {
    if (!selectedChat) return
    scrolledForBundleRef.current = null
    prevMsgCountRef.current = 0
    firstUnreadRef.current = null
    setFilterDepts([])
    setMessage("")
    setAttachedFiles([])
    if (
      typeNotify &&
      [STANDARD_THEME_IDS.CANCELED, STANDARD_THEME_IDS.CORRECTION].includes(typeNotify)
    ) {
      setMessageTheme(typeNotify)
    } else {
      setMessageTheme(null)
    }
    setDepartment(null)
    setShowSidebar(false)
    setReplyTo(null)
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current)
      scrollTimerRef.current = null
    }
  }, [selectedChatId, typeNotify])

  const handleTabChange = useCallback(
    (newTab) => {
      if (newTab === activeTab) return
      setSelectedChat(null)
      setIsMobileView(false)
      dispatch(resetNotifies())
      setActiveTab(newTab)
      setFilters(buildInitialFilters(newTab))
    },
    [activeTab, dispatch]
  )

  const handleSelectChat = useCallback(
    async (chat) => {
      setSelectedChat(chat)
      setReplyTo(null)
      if (isMobile) setIsMobileView(true)
      await fetchMessages(chatId(chat))

      // восстанавливаем черновик
      const draft = loadDraft(chatId(chat))
      if (draft) {
        setMessage(draft.message || "")
        setDepartment(draft.department ?? null)
        setMessageTheme(draft.messageTheme ?? null)
      } else {
        setMessage("")
        setDepartment(null)
        setMessageTheme(null)
      }
    },
    [isMobile, fetchMessages, loadDraft]
  )

  // Открытие по bundleId
  useEffect(() => {
    if (!isOpen || !payload?.bundleId) return
    const openByAnchor = async () => {
      setAnchorLoading(true)
      dispatch(resetNotifies())
      const defaults = TAB_FILTER_DEFAULTS[activeTab] ?? TAB_FILTER_DEFAULTS[CHAT_TABS.ALL]
      try {
        const result = await getListLazy({
          ...defaults,
          anchor_bundle_id: payload.bundleId,
        }).unwrap()
        if (result.chat) {
          dispatch(setNotifies({ items: result.data, meta: result.meta }))
          await handleSelectChat(result.chat)
          setFilters((prev) => ({ ...prev, page: result.meta.current_page + 1 }))
        } else {
          const emptyChat = {
            bundle_id: payload.bundleId,
            message: "",
            company_name: "",
            unread_count: 0,
            created_at: null,
            updated_at: null,
          }
          await handleSelectChat(emptyChat)
        }
      } catch (err) {
        console.error("openByAnchor error", err)
        setFilters({ ...defaults })
      } finally {
        setAnchorLoading(false)
      }
    }
    openByAnchor()
  }, [openTick])

  // Открытие по leadId — переключаемся на таб LEADS и открываем чат лида
  useEffect(() => {
    if (!isOpen || !payload?.leadId) return

    const openByLeadAnchor = async () => {
      setAnchorLoading(true)
      dispatch(resetNotifies())
      setActiveTab(CHAT_TABS.LEADS)

      const defaults = TAB_FILTER_DEFAULTS[CHAT_TABS.LEADS]
      setFilters({ ...defaults, lead_id: String(payload.leadId) })

      try {
        const result = await getLeadNotifiesLazy({
          ...defaults,
          lead_id: String(payload.leadId),
        }).unwrap()

        const chat = result.chat ??
          (result.data?.length > 0 ? result.data[0] : null) ?? {
            bundle_id: payload.leadId,
            message: "",
            lead_name: "",
            unread_count: 0,
            created_at: null,
            updated_at: null,
          }

        setSelectedChat(chat)
        if (isMobile) setIsMobileView(true)

        // FIX: передаём getLeadChatDetailDirect как fetcherOverride (3-й аргумент),
        // чтобы не зависеть от getChatDetailRef — тот ещё не обновился,
        // так как setActiveTab(LEADS) не применился до следующего рендера
        await fetchMessages(chatId(chat), false, getLeadChatDetailDirect)

        if (result.data && result.meta) {
          dispatch(setNotifies({ items: result.data, meta: result.meta }))
          setFilters({
            ...defaults,
            lead_id: String(payload.leadId),
            page: result.meta.current_page + 1,
          })
        }
      } catch (err) {
        console.error("openByLeadAnchor error", err)
        setFilters({ ...TAB_FILTER_DEFAULTS[CHAT_TABS.LEADS] })
      } finally {
        setAnchorLoading(false)
      }
    }

    openByLeadAnchor()
  }, [openTick])

  useEffect(() => {
    if (!isOpen) {
      anchorLoadingRef.current = false
      setIsAnchorLoading(false)
      setSelectedChat(null)
      setIsMobileView(false)
      dispatch(resetNotifies())
      setFilters(buildInitialFilters(activeTab))
      setAttachedFiles([])
      setReplyTo(null)
    }
  }, [isOpen])

  useEffect(() => {
    if (!selectedChat) return
    const bId = selectedChatId
    if (scrolledForBundleRef.current !== bId) return
    if (currentMessagesLength > prevMsgCountRef.current && prevMsgCountRef.current > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }
    prevMsgCountRef.current = currentMessagesLength
  }, [currentMessagesLength, selectedChatId])

  useEffect(() => {
    if (
      !selectedChat ||
      scrolledForBundleRef.current === selectedChatId ||
      currentMessagesLength === 0
    )
      return
    const bId = selectedChatId
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
    scrollTimerRef.current = setTimeout(() => {
      if (scrolledForBundleRef.current === bId) return
      if (firstUnreadIndex !== -1 && firstUnreadRef.current) {
        firstUnreadRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
      }
      scrolledForBundleRef.current = bId
      prevMsgCountRef.current = currentMessagesLength
      scrollTimerRef.current = null
    }, 200)
    return () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current)
        scrollTimerRef.current = null
      }
    }
  }, [currentMessagesLength, firstUnreadIndex, selectedChatId])

  const handleDepartmentChange = useCallback((newDept) => {
    setDepartment(newDept)
    setMessageTheme(null)
  }, [])

  const handleFilterChange = useCallback((key, value) => {
    dispatch(resetNotifies())
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
  }, [])

  const handleFilterSearch = useCallback(() => {
    setFilters((prev) => {
      saveLastFilters(activeTab, prev)
      return prev
    })
  }, [activeTab])

  const handleFilterReset = useCallback(() => {
    clearLastFilters(activeTab)
    setFilters({ ...TAB_FILTER_DEFAULTS[activeTab] })
  }, [activeTab])

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) setFilters((prev) => ({ ...prev, page: prev.page + 1 }))
  }, [isFetching, hasMore])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore()
      },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore])

  useClickOutside(chatRef, () => window.EventBus?.emit(CLOSE_EVENT))

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (!mobile) setIsMobileView(false)
    }
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [])

  const handleBack = () => {
    setIsMobileView(false)
    setSelectedChat(null)
  }

  const handleReply = useCallback(
    (msg) => {
      setReplyTo(msg)
      if (msg?.department_id) setDepartment(msg.department_id)
      if (!IS_TRAVELON && msg?.message_theme) setMessageTheme(msg.message_theme)
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    },
    [IS_TRAVELON]
  )

  const handleCancelReply = useCallback(() => setReplyTo(null), [])

  const handleSend = () => {
    if (!message.trim() || !selectedChat) return
    sendMessage(selectedChatId, {
      text: message,
      department,
      attachedFiles,
      theme: messageTheme,
      for_admin: sendMode === FOR_ADMIN_KEY,
      reply_to_id: replyTo?.id ?? null,
      provider_id: providerId ?? 0,
      remote_order: remoteOrder ?? 0,
    })
    clearDraft(selectedChatId)
    setMessage("")
    setAttachedFiles([])
    setDepartment(null)
    setMessageTheme(null)
    setReplyTo(null)
  }

  const handleUpdateDepartment = async (notifyId, newDeptId) => {
    try {
      await updateDepartment({ id: notifyId, department_id: newDeptId }).unwrap()
      const foundDept = departments?.find((d) => d.id === newDeptId)
      changeDept(selectedChatId, notifyId, newDeptId, foundDept?.name ?? "")
    } catch (error) {
      console.error("Error updating department:", error)
    }
  }

  return (
    <div className="fixed top-0 left-0 z-30">
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              data-chat-panel
              key="backdrop"
              className="fixed inset-0 bg-black/20 z-40"
              variants={MOTION_VARIANTS.backdrop}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              key="panel"
              ref={chatRef}
              className={`fixed top-0 right-0 h-full bg-white shadow-2xl flex z-50 ${
                isResizing || isResizingChat || isResizingCompose ? "select-none" : ""
              }`}
              style={{
                width: isMobile ? "100%" : `${chatWidth}%`,
                minWidth: isMobile ? "100%" : "600px",
                maxWidth: isMobile ? "100%" : "95%",
              }}
              variants={MOTION_VARIANTS.panel}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {!isMobile && (
                <div
                  className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize group z-10"
                  onMouseDown={startResizeChat}
                >
                  <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1 h-16 bg-slate-300 group-hover:bg-blue-500 rounded-r transition-colors" />
                </div>
              )}

              <div className={`flex w-full h-full ${isMobile ? "" : "pl-1"}`}>
                <ChatListSidebar
                  sidebarRef={sidebarRef}
                  isMobileView={isMobileView}
                  selectedChat={selectedChat}
                  isMobile={isMobile}
                  sidebarWidth={sidebarWidth}
                  filteredChats={notifiesList}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onFilterReset={handleFilterReset}
                  onFilterSearch={handleFilterSearch}
                  activeTab={activeTab}
                  setActiveTab={handleTabChange}
                  onSelectChat={handleSelectChat}
                  onClose={() => setIsOpen(false)}
                  onStartResize={startResize}
                  isAdmin={isAdmin}
                  departments={departments}
                  sentinelRef={sentinelRef}
                  isLoadingMore={isFetching || isAnchorLoading}
                  hasMore={hasMore}
                  onCollapse={collapse}
                  onExpand={expand}
                  totalUnread={total_unread}
                  leadsUnread={leads_unread}
                  messagethemes={messagethemesData}
                />

                <motion.div
                  className={`${isMobileView && selectedChat ? "flex" : "hidden md:flex"} flex-1 flex-col bg-white min-w-0`}
                  variants={MOTION_VARIANTS.content}
                  initial="hidden"
                  animate="visible"
                >
                  {selectedChat ? (
                    <>
                      <ChatHeader
                        chat={selectedChat}
                        isMobile={isMobile}
                        onBack={handleBack}
                        onToggleSidebar={() => setShowSidebar((p) => !p)}
                      />

                      {tabCapabilities.showMessageFilter && (
                        <MessageFilter
                          departments={departments}
                          filterDepts={filterDepts}
                          setFilterDepts={setFilterDepts}
                          messages={allMessages}
                        />
                      )}

                      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-100">
                        {isLoading(selectedChatId) ? (
                          <div>{t("admin.loading_dots")}</div>
                        ) : (
                          currentMessages.map((msg, index) => {
                            const msgDate = msg.time ? msg.time.split(" |")[0] : null
                            const prevDate =
                              index > 0 ? currentMessages[index - 1].time?.split(" |")[0] : null
                            const showDivider = msgDate && msgDate !== prevDate
                            const isFirstUnread = index === firstUnreadIndex
                            return (
                              <React.Fragment key={msg.id}>
                                {showDivider && (
                                  <DateDivider
                                    label={formatDateDivider(
                                      msgDate.split(".").reverse().join("-"),
                                      locale
                                    )}
                                  />
                                )}
                                {isFirstUnread && (
                                  <div className="flex items-center gap-3 my-2 px-1">
                                    <div className="flex-1 h-px bg-blue-300" />
                                    <span className="text-xs text-blue-500 font-medium whitespace-nowrap select-none">
                                      {t("admin.unread_messages", "Непрочитанные сообщения")}
                                    </span>
                                    <div className="flex-1 h-px bg-blue-300" />
                                  </div>
                                )}
                                <div ref={isFirstUnread ? firstUnreadRef : null}>
                                  <MessageBubble
                                    msg={msg}
                                    isFirstUnread={isFirstUnread}
                                    onToggleEdit={(id) => toggleEdit(selectedChatId, id)}
                                    onSaveEdit={(id, text) =>
                                      saveEdit(selectedChatId, id, text, updateNotifyMessage)
                                    }
                                    onCancelEdit={(id) => cancelEdit(selectedChatId, id)}
                                    onChangeDept={
                                      tabCapabilities.isLeads
                                        ? null
                                        : (id, dept) => handleUpdateDepartment(id, dept)
                                    }
                                    toggleReadStatus={(id) =>
                                      toggleReadStatus(selectedChatId, msg.id, {
                                        toggleReadMsg,
                                        currentUserId,
                                      })
                                    }
                                    departments={tabCapabilities.isLeads ? [] : departments}
                                    isAdmin={isAdmin}
                                    currentCompanyId={currentCompanyId}
                                    onReply={tabCapabilities.canSendMessage ? handleReply : null}
                                    allMessages={allMessages}
                                  />
                                </div>
                              </React.Fragment>
                            )
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      {tabCapabilities.canSendMessage && (
                        <ComposeArea
                          message={message}
                          setMessage={setMessage}
                          setDepartment={
                            tabCapabilities.isLeads ? undefined : handleDepartmentChange
                          }
                          attachedFiles={attachedFiles}
                          setAttachedFiles={setAttachedFiles}
                          onSend={handleSend}
                          departments={tabCapabilities.isLeads ? [] : departments}
                          department={tabCapabilities.isLeads ? null : department}
                          filteredMessagethemes={filteredMessagethemes}
                          setMessageTheme={tabCapabilities.isLeads ? undefined : setMessageTheme}
                          messageTheme={tabCapabilities.isLeads ? null : messageTheme}
                          setSendMode={setSendMode}
                          activeSendMode={sendMode}
                          isAdmin={isAdmin}
                          replyTo={replyTo}
                          onCancelReply={handleCancelReply}
                          useDistribution={tabCapabilities.isLeads ? false : useDistribution}
                          messageSubjectIsRequired={
                            tabCapabilities.isLeads ? false : settings?.message_subject_is_required
                          }
                          composeHeight={composeHeight}
                          onStartResizeCompose={startResizeCompose}
                          onDraftChange={(data) => saveDraftDebounced(selectedChatId, data)}
                          bundleId={selectedChat?.bundle_id}
                          autofillStandardThemeIds={
                            settings?.autofill_description_for_standard_themes
                          }
                          autofillStandardThemesScope={
                            settings?.autofill_description_for_standard_themes_scope
                          }
                        />
                      )}
                    </>
                  ) : (
                    <EmptyState />
                  )}
                </motion.div>

                <AnimatePresence>
                  {showSidebar && selectedChat && !tabCapabilities.isLeads && (
                    <ChatInfoSidebar
                      key="info"
                      chat={selectedChat}
                      onClose={() => setShowSidebar(false)}
                    />
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ChatBox
